/*
 * Indonesian tax engine — PPh 21 (PP 58/2023 TER, PMK 168/2023), PPh 23, PPh Final 4 ayat (2).
 * All amounts in Rupiah.
 */

export const PTKP_STATUSES = ["TK/0", "TK/1", "TK/2", "TK/3", "K/0", "K/1", "K/2", "K/3"];

const I = Infinity;
// [upper bound (inclusive), rate %]
export const TER = {
  A: [[5400000, 0], [5650000, 0.25], [5950000, 0.5], [6300000, 0.75], [6750000, 1], [7500000, 1.25], [8550000, 1.5], [9650000, 1.75], [10050000, 2], [10350000, 2.25], [10700000, 2.5], [11050000, 3], [11600000, 3.5], [12500000, 4], [13750000, 5], [15100000, 6], [16950000, 7], [19750000, 8], [24150000, 9], [26450000, 10], [28000000, 11], [30050000, 12], [32400000, 13], [35400000, 14], [39100000, 15], [43850000, 16], [47800000, 17], [51400000, 18], [56300000, 19], [62200000, 20], [68600000, 21], [77500000, 22], [89000000, 23], [103000000, 24], [125000000, 25], [157000000, 26], [206000000, 27], [337000000, 28], [454000000, 29], [550000000, 30], [695000000, 31], [910000000, 32], [1400000000, 33], [I, 34]],
  B: [[6200000, 0], [6500000, 0.25], [6850000, 0.5], [7300000, 0.75], [9200000, 1], [10750000, 1.5], [11250000, 2], [11600000, 2.5], [12600000, 3], [13600000, 4], [14950000, 5], [16400000, 6], [18450000, 7], [21850000, 8], [26000000, 9], [27700000, 10], [29350000, 11], [31450000, 12], [33950000, 13], [37100000, 14], [41100000, 15], [45800000, 16], [49500000, 17], [53800000, 18], [58500000, 19], [64000000, 20], [71000000, 21], [80000000, 22], [93000000, 23], [109000000, 24], [129000000, 25], [163000000, 26], [211000000, 27], [374000000, 28], [459000000, 29], [555000000, 30], [704000000, 31], [957000000, 32], [1405000000, 33], [I, 34]],
  C: [[6600000, 0], [6950000, 0.25], [7350000, 0.5], [7800000, 0.75], [8850000, 1], [9800000, 1.25], [10950000, 1.5], [11200000, 1.75], [12050000, 2], [12950000, 3], [14150000, 4], [15550000, 5], [17050000, 6], [19500000, 7], [22700000, 8], [26600000, 9], [28100000, 10], [30100000, 11], [32600000, 12], [35400000, 13], [38900000, 14], [43000000, 15], [47400000, 16], [51200000, 17], [55800000, 18], [60400000, 19], [66700000, 20], [74500000, 21], [83200000, 22], [95600000, 23], [110000000, 24], [134000000, 25], [169000000, 26], [221000000, 27], [390000000, 28], [463000000, 29], [561000000, 30], [709000000, 31], [965000000, 32], [1419000000, 33], [I, 34]],
};

export const PASAL17 = [
  [60000000, 5],
  [250000000, 15],
  [500000000, 25],
  [5000000000, 30],
  [I, 35],
];

export const JKK_OPTIONS = [
  { value: 0.24, label: "0,24% – risiko sangat rendah" },
  { value: 0.54, label: "0,54% – risiko rendah" },
  { value: 0.89, label: "0,89% – risiko sedang" },
  { value: 1.27, label: "1,27% – risiko tinggi" },
  { value: 1.74, label: "1,74% – risiko sangat tinggi" },
];

export const r0 = (x) => Math.round(x || 0);
export const floorThousand = (x) => Math.floor(Math.max(0, x) / 1000) * 1000;

export function ptkpAmount(status) {
  const married = status.startsWith("K/");
  const deps = Math.min(3, parseInt(status.split("/")[1] || "0", 10) || 0);
  return 54000000 + (married ? 4500000 : 0) + deps * 4500000;
}

export function terCategory(status) {
  if (["TK/0", "TK/1", "K/0"].includes(status)) return "A";
  if (["TK/2", "TK/3", "K/1", "K/2"].includes(status)) return "B";
  return "C";
}

export function terRate(cat, bruto) {
  for (const [ub, r] of TER[cat]) if (bruto <= ub) return r;
  return 34;
}

export function pasal17(pkp) {
  let prev = 0;
  let tax = 0;
  for (const [ub, r] of PASAL17) {
    if (pkp > prev) {
      tax += ((Math.min(pkp, ub) - prev) * r) / 100;
      prev = ub;
    }
  }
  return tax;
}

export function pasal17Layers(pkp) {
  let prev = 0;
  const out = [];
  for (const [ub, r] of PASAL17) {
    if (pkp > prev) {
      const portion = Math.min(pkp, ub) - prev;
      out.push({ from: prev, to: Math.min(pkp, ub), rate: r, portion, tax: (portion * r) / 100 });
      prev = ub;
    }
  }
  return out;
}

/** Gross-up with TER: find T such that T = rate(B+T) * (B+T). */
function grossUpTer(cat, base, mult) {
  let lower = 0;
  for (const [ub, r] of TER[cat]) {
    if (ub < base) {
      lower = ub;
      continue;
    }
    const rr = (r * mult) / 100;
    const G = base / (1 - rr);
    if (G <= ub && G >= lower) return G - base;
    lower = ub;
  }
  return 0;
}

export const PPH21_DEFAULT = {
  employeeType: "tetap",
  tetap: {
    inputBasis: "bruto",
    targetTakeHome: 10000000,
    gajiPokok: 10000000,
    tunjanganTetap: 1500000,
    tunjanganLain: 0,
    bonus: 0,
    bonusMonth: 12,
    thr: 0,
    thrMonth: 4,
    status: "TK/0",
    npwp: true,
    method: "gross",
    bpjs: {
      enabled: true,
      jkkRate: 0.24,
      jkmRate: 0.3,
      kes: true,
      kesCap: 12000000,
      jht: true,
      jp: true,
      jpCap: 10547400,
    },
  },
  tidakTetap: {
    payMode: "harian",
    upahHarian: 300000,
    hariKerja: 22,
    upahBulanan: 6000000,
    status: "TK/0",
    npwp: true,
    method: "gross",
  },
  bukanPegawai: {
    inputBasis: "bruto",
    amount: 15000000,
    kumulatif: 0,
    npwp: true,
    jenis: "Tenaga ahli / konsultan",
  },
};

/** PPh 21 Pegawai Tetap — Jan–Nov TER, Desember rekonsiliasi Pasal 17. */
export function calcPph21Tetap(inp) {
  const p = inp;
  const b = p.bpjs || {};
  const cat = terCategory(p.status);
  const mult = p.npwp ? 1 : 1.2;
  const ptkp = ptkpAmount(p.status);
  const gp = +p.gajiPokok || 0;
  const tt = +p.tunjanganTetap || 0;
  const tl = +p.tunjanganLain || 0;
  const upah = gp + tt;
  const on = !!b.enabled;
  const jkk = on ? (upah * (+b.jkkRate || 0)) / 100 : 0;
  const jkm = on ? (upah * (+b.jkmRate || 0)) / 100 : 0;
  const kesBase = Math.min(upah, +b.kesCap || upah);
  const kesEr = on && b.kes ? kesBase * 0.04 : 0;
  const kesEe = on && b.kes ? kesBase * 0.01 : 0;
  const jhtEr = on && b.jht ? upah * 0.037 : 0;
  const jhtEe = on && b.jht ? upah * 0.02 : 0;
  const jpBase = Math.min(upah, +b.jpCap || upah);
  const jpEr = on && b.jp ? jpBase * 0.02 : 0;
  const jpEe = on && b.jp ? jpBase * 0.01 : 0;

  const months = [];
  for (let m = 1; m <= 12; m++) {
    const irregular = (+p.bonusMonth === m ? +p.bonus || 0 : 0) + (+p.thrMonth === m ? +p.thr || 0 : 0);
    const base = gp + tt + tl + irregular + jkk + jkm + kesEr;
    months.push({ month: m, gp, tt, tl, irregular, jkk, jkm, kesEr, base });
  }

  // Jan–Nov
  for (let i = 0; i < 11; i++) {
    const row = months[i];
    let T = 0;
    if (p.method === "grossup") T = grossUpTer(cat, row.base, mult);
    const bruto = row.base + T;
    const rate = terRate(cat, bruto);
    let pph = r0((bruto * rate * mult) / 100);
    if (p.method === "grossup") T = pph;
    Object.assign(row, { tunjanganPajak: T, bruto: row.base + T, terRate: rate, pph, reconciliation: false });
  }

  const sum11 = months.slice(0, 11).reduce((a, r) => a + r.pph, 0);
  const bruto11 = months.slice(0, 11).reduce((a, r) => a + r.bruto, 0);
  const iuranSetahun = 12 * (jhtEe + jpEe);

  const annual = (totalBruto) => {
    const biayaJabatan = Math.min(totalBruto * 0.05, 6000000);
    const neto = totalBruto - biayaJabatan - iuranSetahun;
    const pkp = floorThousand(neto - ptkp);
    const taxRaw = pasal17(pkp);
    const tax = r0(taxRaw * mult);
    return { totalBruto, biayaJabatan, iuran: iuranSetahun, neto, ptkp, pkp, tax, layers: pasal17Layers(pkp) };
  };

  const dec = months[11];
  let Tdec = 0;
  let ann;
  if (p.method === "grossup") {
    for (let k = 0; k < 80; k++) {
      ann = annual(bruto11 + dec.base + Tdec);
      const next = ann.tax - sum11;
      if (Math.abs(next - Tdec) < 1) {
        Tdec = next;
        break;
      }
      Tdec = next;
    }
    ann = annual(bruto11 + dec.base + Tdec);
    Tdec = ann.tax - sum11;
  } else {
    ann = annual(bruto11 + dec.base);
  }
  const pphDec = ann.tax - sum11;
  Object.assign(dec, { tunjanganPajak: Tdec, bruto: dec.base + Tdec, terRate: null, pph: pphDec, reconciliation: true });

  for (const row of months) {
    const cash = row.gp + row.tt + row.tl + row.irregular;
    const deductEmployee = jhtEe + jpEe + kesEe;
    row.potonganBpjs = deductEmployee;
    row.thp = cash + row.tunjanganPajak - (p.method === "net" ? 0 : row.pph) - deductEmployee;
    row.companyCost = cash + jkk + jkm + kesEr + jhtEr + jpEr + row.tunjanganPajak + (p.method === "net" ? row.pph : 0);
  }

  const regular = months.find((r, i) => i < 11 && r.irregular === 0) || months[0];
  const totalPph = months.reduce((a, r) => a + r.pph, 0);
  return {
    category: cat,
    ptkp,
    mult,
    months,
    annual: ann,
    sum11,
    pphDec,
    totalPph,
    regular,
    thpAnnual: months.reduce((a, r) => a + r.thp, 0),
    costAnnual: months.reduce((a, r) => a + r.companyCost, 0),
    effectiveRate: ann.totalBruto ? (ann.tax / ann.totalBruto) * 100 : 0,
    bpjs: { jkk, jkm, kesEr, kesEe, jhtEr, jhtEe, jpEr, jpEe },
  };
}

/** Solve gaji pokok so that a regular month's take-home equals target. */
export function solveGajiForTakeHome(inp) {
  const target = +inp.targetTakeHome || 0;
  if (target <= 0) return 0;
  let lo = 0;
  let hi = target * 3 + 5000000;
  for (let i = 0; i < 70; i++) {
    const mid = (lo + hi) / 2;
    const res = calcPph21Tetap({ ...inp, gajiPokok: mid });
    if (res.regular.thp >= target) hi = mid;
    else lo = mid;
  }
  return Math.ceil(hi);
}

export function runPph21Tetap(inp) {
  if (inp.inputBasis === "takehome") {
    const gp = solveGajiForTakeHome(inp);
    return { ...calcPph21Tetap({ ...inp, gajiPokok: gp }), solvedGajiPokok: gp };
  }
  return calcPph21Tetap(inp);
}

/** PPh 21 Pegawai Tidak Tetap (harian / bulanan). */
export function calcPph21TidakTetap(inp) {
  const mult = inp.npwp ? 1 : 1.2;
  if (inp.payMode === "bulanan") {
    const cat = terCategory(inp.status);
    const base = +inp.upahBulanan || 0;
    let T = inp.method === "grossup" ? grossUpTer(cat, base, mult) : 0;
    const bruto = base + T;
    const rate = terRate(cat, bruto);
    const pph = r0((bruto * rate * mult) / 100);
    if (inp.method === "grossup") T = pph;
    return {
      scheme: `TER Bulanan Kategori ${cat}`,
      category: cat,
      dailyBruto: null,
      totalBruto: base + T,
      tunjanganPajak: T,
      rate,
      pph,
      net: base + T - pph,
      note: "Pegawai tidak tetap yang menerima upah bulanan dipotong dengan TER bulanan sesuai status PTKP.",
    };
  }
  const daily = +inp.upahHarian || 0;
  const days = +inp.hariKerja || 0;
  const compute = (d) => {
    const total = d * days;
    if (d <= 2500000) {
      const rate = d <= 450000 ? 0 : 0.5;
      return { total, rate, pph: r0((total * rate * mult) / 100), scheme: "TER Harian" };
    }
    const dpp = total * 0.5;
    const tax = r0(pasal17(dpp) * mult);
    return { total, rate: total ? (tax / total) * 100 : 0, pph: tax, scheme: "Tarif Pasal 17 × 50% bruto (upah harian > Rp2.500.000)" };
  };
  let d = daily;
  let res = compute(d);
  let T = 0;
  if (inp.method === "grossup" && days > 0) {
    for (let k = 0; k < 60; k++) {
      const next = daily + res.pph / days;
      if (Math.abs(next - d) < 0.01) break;
      d = next;
      res = compute(d);
    }
    T = res.total - daily * days;
  }
  return {
    scheme: res.scheme,
    dailyBruto: d,
    totalBruto: res.total,
    tunjanganPajak: T,
    rate: res.rate,
    pph: res.pph,
    net: res.total - res.pph,
    note:
      "Upah harian s.d. Rp450.000 tarif 0%, di atas Rp450.000 s.d. Rp2.500.000 tarif efektif harian 0,5%. Di atas Rp2.500.000/hari memakai tarif Pasal 17 atas 50% penghasilan bruto.",
  };
}

/** PPh 21 Bukan Pegawai — tarif Pasal 17 × 50% penghasilan bruto (kumulatif bila berkesinambungan). */
export function calcPph21BukanPegawai(inp) {
  const mult = inp.npwp ? 1 : 1.2;
  const prevDpp = (+inp.kumulatif || 0) * 0.5;
  const taxFor = (bruto) => r0((pasal17(prevDpp + bruto * 0.5) - pasal17(prevDpp)) * mult);
  let bruto = +inp.amount || 0;
  if (inp.inputBasis === "net") {
    const net = bruto;
    let g = net;
    for (let k = 0; k < 100; k++) {
      const next = net + taxFor(g);
      if (Math.abs(next - g) < 0.5) {
        g = next;
        break;
      }
      g = next;
    }
    bruto = Math.round(g);
  }
  const dpp = bruto * 0.5;
  const pph = taxFor(bruto);
  return {
    bruto,
    dpp,
    pph,
    net: bruto - pph,
    effectiveRate: bruto ? (pph / bruto) * 100 : 0,
    layers: pasal17Layers(prevDpp + dpp),
    prevDpp,
  };
}

/* ---------------- PPh 23 ---------------- */
export const PPH23_TYPES = [
  { key: "jasa", label: "Jasa (teknik, manajemen, konsultan, IT, maintenance, dll)", rate: 2 },
  { key: "sewa", label: "Sewa harta selain tanah/bangunan (kendaraan, alat, server)", rate: 2 },
  { key: "dividen", label: "Dividen", rate: 15 },
  { key: "bunga", label: "Bunga (termasuk premium, diskonto)", rate: 15 },
  { key: "royalti", label: "Royalti", rate: 15 },
  { key: "hadiah", label: "Hadiah, penghargaan, bonus (selain yang dipotong PPh 21)", rate: 15 },
];

export const PPH23_DEFAULT = {
  type: "jasa",
  amount: 10000000,
  basis: "dpp",
  npwp: true,
  ppn: { enabled: true, mode: "dpp_nilai_lain", customRate: 11 },
};

export function ppnEffective(ppn) {
  if (!ppn?.enabled) return 0;
  if (ppn.mode === "full12") return 0.12;
  if (ppn.mode === "custom") return (+ppn.customRate || 0) / 100;
  return 0.12 * (11 / 12);
}

export function calcPph23(inp) {
  const t = PPH23_TYPES.find((x) => x.key === inp.type) || PPH23_TYPES[0];
  const rate = t.rate * (inp.npwp ? 1 : 2);
  const r = rate / 100;
  const ppnRate = ppnEffective(inp.ppn);
  const amt = +inp.amount || 0;
  let dpp = amt;
  if (inp.basis === "incl_ppn") dpp = amt / (1 + ppnRate);
  if (inp.basis === "net") dpp = amt / (1 - r);
  dpp = Math.round(dpp);
  const ppn = Math.round(dpp * ppnRate);
  const pph = Math.round(dpp * r);
  const invoice = dpp + ppn;
  return {
    type: t,
    rate,
    ppnRate: ppnRate * 100,
    dpp,
    dppNilaiLain: inp.ppn?.enabled && inp.ppn.mode === "dpp_nilai_lain" ? Math.round((dpp * 11) / 12) : null,
    ppn,
    pph,
    invoice,
    paidToVendor: invoice - pph,
    vendorNetExPpn: dpp - pph,
  };
}

/* ---------------- PPh 4 ayat (2) ---------------- */
export const PPH42_TYPES = [
  { key: "sewa_tanah_bangunan", group: "Sewa", label: "Sewa tanah dan/atau bangunan", rate: 10, ppn: true },
  { key: "konstruksi_kecil", group: "Jasa Konstruksi (PP 9/2022)", label: "Pelaksana konstruksi – kualifikasi kecil / perseorangan bersertifikat", rate: 1.75, ppn: true },
  { key: "konstruksi_menengah_besar", group: "Jasa Konstruksi (PP 9/2022)", label: "Pelaksana konstruksi – kualifikasi menengah / besar", rate: 2.65, ppn: true },
  { key: "konstruksi_tanpa_sertifikat", group: "Jasa Konstruksi (PP 9/2022)", label: "Pelaksana konstruksi – tanpa sertifikat badan usaha / kompetensi", rate: 4, ppn: true },
  { key: "konsultansi_bersertifikat", group: "Jasa Konstruksi (PP 9/2022)", label: "Konsultansi konstruksi – bersertifikat", rate: 3.5, ppn: true },
  { key: "konsultansi_tanpa_sertifikat", group: "Jasa Konstruksi (PP 9/2022)", label: "Konsultansi konstruksi – tanpa sertifikat", rate: 6, ppn: true },
  { key: "terintegrasi_bersertifikat", group: "Jasa Konstruksi (PP 9/2022)", label: "Pekerjaan konstruksi terintegrasi – bersertifikat", rate: 2.65, ppn: true },
  { key: "terintegrasi_tanpa_sertifikat", group: "Jasa Konstruksi (PP 9/2022)", label: "Pekerjaan konstruksi terintegrasi – tanpa sertifikat", rate: 4, ppn: true },
  { key: "pengalihan_tanah_bangunan", group: "Tanah & Bangunan", label: "Pengalihan hak atas tanah dan/atau bangunan", rate: 2.5, ppn: false },
  { key: "rumah_sederhana", group: "Tanah & Bangunan", label: "Pengalihan rumah / rusun sederhana (pengembang)", rate: 1, ppn: false },
  { key: "umkm", group: "UMKM", label: "PPh Final UMKM atas omzet (PP 55/2022)", rate: 0.5, ppn: false },
  { key: "bunga_deposito", group: "Investasi", label: "Bunga deposito / tabungan / diskonto SBI", rate: 20, ppn: false },
  { key: "bunga_obligasi", group: "Investasi", label: "Bunga / diskonto obligasi", rate: 10, ppn: false },
  { key: "dividen_op", group: "Investasi", label: "Dividen untuk WP orang pribadi dalam negeri", rate: 10, ppn: false },
  { key: "saham_bursa", group: "Investasi", label: "Penjualan saham di bursa efek (dari nilai transaksi)", rate: 0.1, ppn: false },
  { key: "hadiah_undian", group: "Lainnya", label: "Hadiah undian", rate: 25, ppn: false },
];

export const PPH42_DEFAULT = {
  type: "sewa_tanah_bangunan",
  amount: 50000000,
  basis: "gross",
  ppn: { enabled: false, mode: "dpp_nilai_lain", customRate: 11 },
  umkm: { wpType: "op", omzetKumulatif: 0 },
};

export function calcPph42(inp) {
  const t = PPH42_TYPES.find((x) => x.key === inp.type) || PPH42_TYPES[0];
  const r = t.rate / 100;
  const ppnRate = t.ppn ? ppnEffective(inp.ppn) : 0;
  const amt = +inp.amount || 0;
  let bruto = amt;
  if (inp.basis === "net") bruto = amt / (1 - r);
  bruto = Math.round(bruto);
  let taxable = bruto;
  let umkmNote = null;
  if (t.key === "umkm" && inp.umkm?.wpType === "op") {
    const prev = +inp.umkm.omzetKumulatif || 0;
    const threshold = 500000000;
    const exempt = Math.max(0, Math.min(bruto, threshold - prev));
    taxable = bruto - exempt;
    umkmNote = `Omzet s.d. Rp500 juta per tahun untuk WP orang pribadi tidak dikenai PPh. Bagian omzet bebas pada periode ini: Rp${exempt.toLocaleString("id-ID")}.`;
  }
  const pph = Math.round(taxable * r);
  const ppn = Math.round(bruto * ppnRate);
  return {
    type: t,
    rate: t.rate,
    bruto,
    taxable,
    pph,
    ppn,
    ppnRate: ppnRate * 100,
    invoice: bruto + ppn,
    paidToRecipient: bruto + ppn - (t.key === "umkm" ? 0 : pph),
    netRecipient: bruto - pph,
    umkmNote,
  };
}

export const TAX_DEFAULT = () => ({
  tab: "pph21",
  pph21: JSON.parse(JSON.stringify(PPH21_DEFAULT)),
  pph23: JSON.parse(JSON.stringify(PPH23_DEFAULT)),
  pph42: JSON.parse(JSON.stringify(PPH42_DEFAULT)),
});
